import React, { useMemo, useState } from "react";

import LockedContent from "../../../../grace-period/components/LockedContent";
import { useAccessStatus } from "../../../../grace-period/hooks/useAccessStatus";
import SwabList from "../SwabList/SwabList";

import "./ResultSummary.css";

const RESULT_ITEMS = [
  {
    key: "out_of_spec",
    label: "Out Of Spec",
    valueKey: "num_out_of_spec",
    color: "var(--color-content-negative)",
  },
  {
    key: "in_spec",
    label: "In Spec",
    valueKey: "num_in_spec",
    color: "var(--color-content-positive)",
  },
  {
    key: "incomplete",
    label: "Incomplete Specs",
    valueKey: "num_incomplete_spec",
    color: "var(--color-content-warning)",
  },
];

const ResultSummary = ({ specDetails, dateRange = "" }) => {
  const { areResultsLocked } = useAccessStatus();
  const [swabListModal, setSwabListModal] = useState({
    open: false,
    category: null,
  });

  const items = useMemo(() => RESULT_ITEMS.map((item) => ({
    ...item,
    value: specDetails[item.valueKey],
  })), [specDetails]);

  const total = items.reduce((sum, item) => sum + item.value, 0);

  const barSegments = items.map((item) => ({
    ...item,
    widthPercent: total > 0 ? (item.value / total) * 100 : 100 / items.length,
  }));

  const handleOpenSwabList = (item, event) => {
    if (event) {
      event.stopPropagation();
    }
    setSwabListModal({
      open: true,
      category: item.key,
    });
  };

  const handleCloseSwabList = () => {
    setSwabListModal({
      open: false,
      category: null,
    });
  };

  return (
    <div className="ResultSummary">
      <div className="ResultSummary__Header">Result Summary</div>
      <div className="ResultSummary__Body">
        {areResultsLocked ? (
          <LockedContent variant="summary" />
        ) : (
          <>
            <div className="ResultSummary__Bar">
              {barSegments.map((segment) => (
                <div
                  key={segment.key}
                  className="ResultSummary__BarSegment"
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${segment.label} swab list`}
                  onClick={(event) => handleOpenSwabList(segment, event)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleOpenSwabList(segment, event);
                    }
                  }}
                  style={{
                    width: `${segment.widthPercent}%`,
                    backgroundColor: segment.color,
                  }}
                />
              ))}
            </div>
            <div className="ResultSummary__List">
              {items.map((item) => (
                <div
                  key={item.key}
                  className="ResultSummary__ListItem"
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${item.label} swab list`}
                  onClick={(event) => handleOpenSwabList(item, event)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleOpenSwabList(item, event);
                    }
                  }}
                >
                  <div className="ResultSummary__ListItemLabel">
                    <span
                      className="ResultSummary__Dot"
                      style={{ backgroundColor: item.color }}
                    />
                    <span>{item.label}</span>
                  </div>
                  <div className="ResultSummary__ListItemRight">
                    <span className="ResultSummary__ListItemValue">{item.value}</span>
                    <button
                      type="button"
                      className="ResultSummary__ListItemAction"
                      onClick={(event) => handleOpenSwabList(item, event)}
                    >
                      View swab list
                      <span className="ResultSummary__ListItemActionArrow" aria-hidden="true">→</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {swabListModal.open && (
        <SwabList
          open={swabListModal.open}
          onClose={handleCloseSwabList}
          category={swabListModal.category}
          dateRange={dateRange}
        />
      )}
    </div>
  );
};

export default ResultSummary;
